"use client";

// ============================================================
// LivePulse — 카미봇 공식 지원 서버의 월간 공지 + Q&A
// 데이터 형태는 Discord webhook / message shape를 그대로 따라요.
// 추후 MOCK_*를 fetch 결과로 교체하면 컴포넌트는 그대로 동작합니다.
// (가이드 §2.5 / §5.3)
// ============================================================

import { useState } from "react";
import { I, Avatar } from "@/components/kamibot-v2/discord";

// ---- Types (Discord webhook shape) ----
type AnnouncementHighlight = {
  kind: "feature" | "fix" | "polish";
  label: string;
  title: string;
  body: string;
};

type Announcement = {
  id: string;
  channel: string;
  publishedAt: string;
  edition: string;
  title: string;
  intro: string;
  highlights: AnnouncementHighlight[];
  stats: { servers: string; uptime: string; commands: string };
  reactions: Array<{ emoji: string; count: number }>;
};

type QAItem = {
  id: string;
  askedAt: string;
  asker: { name: string; color: number };
  question: string;
  answers: Array<{ who: string; isBot?: boolean; body: string }>;
  resolved: boolean;
  tag: string;
};

// ---- Mock data ----
// TODO: GET /api/kamibot/announcement/latest — 백엔드 연결은 후속 작업 (가이드 §5.3)
const MOCK_ANNOUNCEMENT: Announcement = {
  id: "1264-2026-04",
  channel: "공지사항",
  publishedAt: "2026-04-22T14:00:00+09:00",
  edition: "2026년 4월 월간 공지",
  title: "이달의 카미봇 — 4월 업데이트",
  intro:
    "안녕하세요! 이달도 카미봇과 함께해 주셔서 고마워요. 이번 달엔 TTS 음성 12개가 새로 들어왔고, AI 어시스턴트가 첨부 이미지를 읽기 시작했어요.",
  highlights: [
    { kind: "feature", label: "NEW", title: "TTS 음성 12개 추가", body: "VOICEVOX 신규 캐릭터 8명, ElevenLabs 한국어 4명." },
    { kind: "feature", label: "NEW", title: "AI가 이미지를 봐요", body: "@카미봇 멘션할 때 첨부한 이미지를 같이 읽어드려요." },
    { kind: "fix", label: "FIX", title: "환영 카드 깨짐 수정", body: "특정 닉네임에서 카드 렌더가 잘리던 문제를 고쳤어요." },
    { kind: "polish", label: "POLISH", title: "/도와줘 응답 속도", body: "평균 1.4s → 0.6s." },
  ],
  stats: { servers: "+1,243", uptime: "99.94%", commands: "2.1M" },
  reactions: [
    { emoji: "🩷", count: 482 },
    { emoji: "🎉", count: 217 },
    { emoji: "👀", count: 88 },
    { emoji: "🐱", count: 64 },
  ],
};

// TODO: GET /api/kamibot/qa/recent?limit=5 — 백엔드 연결은 후속 작업 (가이드 §5.3)
const MOCK_QA: QAItem[] = [
  {
    id: "q-2841",
    askedAt: "2시간 전",
    asker: { name: "rin_pop", color: 250 },
    question: "TTS 음성을 채널마다 다르게 둘 수 있나요?",
    answers: [
      { who: "kamibot", isBot: true, body: "네! /tts 설정 채널 선택 으로 채널별 기본 목소리를 따로 둘 수 있어요." },
    ],
    resolved: true,
    tag: "TTS",
  },
  {
    id: "q-2840",
    askedAt: "5시간 전",
    asker: { name: "yuki_dev", color: 145 },
    question: "@카미봇 한국어 외 일본어로도 자연스럽게 답해줘요?",
    answers: [
      { who: "kamibot", isBot: true, body: "메시지 언어를 자동으로 감지해서 같은 언어로 답해요. 일본어 서버에서 잘 굴러갑니다." },
      { who: "yuki_dev", body: "오 좋네요 시도해볼게요" },
    ],
    resolved: true,
    tag: "AI",
  },
  {
    id: "q-2839",
    askedAt: "어제",
    asker: { name: "moss", color: 30 },
    question: "임시 음성 채널 자동 삭제까지 시간 조절 가능?",
    answers: [
      { who: "kamibot", isBot: true, body: "대시보드 → 음성 채널 → 자동 정리에서 30초~24시간 사이로 둘 수 있어요." },
    ],
    resolved: true,
    tag: "VOICE",
  },
  {
    id: "q-2837",
    askedAt: "이틀 전",
    asker: { name: "haru.k", color: 320 },
    question: "환영 카드 배경에 우리 서버 로고 넣고 싶어요",
    answers: [
      { who: "kamibot", isBot: true, body: "이미지 업로드 기능은 5월에 들어와요. 그 전까지는 색상·그라디언트로만 가능해요!" },
    ],
    resolved: false,
    tag: "WELCOME",
  },
  {
    id: "q-2835",
    askedAt: "사흘 전",
    asker: { name: "kalmae", color: 200 },
    question: "프리미엄 가격은 정해졌나요?",
    answers: [
      { who: "kamibot", isBot: true, body: "아직 확정 전이에요. 멤버십 섹션의 알림 신청에 메일 남겨주시면 가장 먼저 알려드려요!" },
    ],
    resolved: true,
    tag: "PREMIUM",
  },
];

// ---- Helpers ----
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

const KIND_STYLES: Record<AnnouncementHighlight["kind"], { color: string; bg: string }> = {
  feature: { color: "var(--kbv2-primary-strong)", bg: "var(--kbv2-primary-soft)" },
  fix: { color: "oklch(0.45 0.13 145)", bg: "oklch(0.94 0.06 145)" },
  polish: { color: "oklch(0.45 0.13 260)", bg: "oklch(0.94 0.05 260)" },
};

// ============================================================
// AnnouncementCard — 고정된 디스코드 공지처럼
// ============================================================
function AnnouncementCard({ data }: { data: Announcement }) {
  return (
    <article className="ann-card">
      <header className="ann-head">
        <div className="ann-channel">
          <I.hash />
          <span>{data.channel}</span>
          <span className="ann-pin" title="고정된 메시지">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14 2l8 8-5 1-3 3 1 5-3-3-7 7v-3l7-7-3-3 1-5 4-3z" />
            </svg>
            고정됨
          </span>
        </div>
        <span className="ann-edition mono">{data.edition}</span>
      </header>

      <div className="ann-msg">
        <Avatar />
        <div className="ann-body">
          <div className="ann-author">
            <span className="name bot">카미봇</span>
            <span className="badge-bot is-verified">앱</span>
            <span className="ts">{fmtDate(data.publishedAt)}</span>
          </div>
          <h3 className="ann-title">{data.title}</h3>
          <p className="ann-intro">{data.intro}</p>

          <div className="ann-embed">
            <ul className="ann-highlights">
              {data.highlights.map((h, i) => (
                <li key={i}>
                  <span
                    className="ann-kind mono"
                    style={{ color: KIND_STYLES[h.kind]?.color, background: KIND_STYLES[h.kind]?.bg }}
                  >
                    {h.label}
                  </span>
                  <div className="ann-h-body">
                    <b>{h.title}</b>
                    <span>{h.body}</span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="ann-stats">
              <div>
                <b>{data.stats.servers}</b>
                <span>이번 달 새 서버</span>
              </div>
              <div className="div" />
              <div>
                <b>{data.stats.uptime}</b>
                <span>업타임</span>
              </div>
              <div className="div" />
              <div>
                <b>{data.stats.commands}</b>
                <span>처리한 명령어</span>
              </div>
            </div>
          </div>

          <div className="ann-reactions">
            {data.reactions.map((r, i) => (
              <button key={i} className="ann-react" type="button">
                <span>{r.emoji}</span>
                <small>{r.count}</small>
              </button>
            ))}
            <button className="ann-react add" type="button" title="반응 추가">
              <I.plus />
            </button>
          </div>
        </div>
      </div>

      <footer className="ann-foot">
        <span className="ann-meta">
          <span className="dot" /> 카미봇 공식 지원 서버에서 매월 첫째 주에 공지해요
        </span>
        <a className="ann-link">
          전체 공지 보기 <I.arrow />
        </a>
      </footer>
    </article>
  );
}

// ============================================================
// QAList — 지원 서버 포럼을 둘러보는 느낌
// ============================================================
function QAList({ items }: { items: QAItem[] }) {
  const [open, setOpen] = useState<string | null>(items[0]?.id ?? null);

  return (
    <div className="qa">
      <div className="qa-head">
        <div>
          <span className="section-eyebrow" style={{ marginBottom: 8, display: "inline-flex" }}>
            SUPPORT · LIVE
          </span>
          <h3 className="qa-title">지원 서버에서 오간 최근 질문</h3>
          <p className="qa-sub">카미봇 공식 지원 서버에서 자주 올라오는 질문과 그 답을 그대로 가져왔어요.</p>
        </div>
        <a className="qa-cta qa-cta-stack" href="https://discord.gg/kamibot" target="_blank" rel="noopener noreferrer">
          <span className="qa-cta-row">
            <I.discord /> <span>지원 서버 들어가기</span> <I.arrow className="qa-cta-arrow" />
          </span>
          <span className="qa-cta-meta">
            <span className="qa-cta-dot" /> 멤버 12,345명
          </span>
        </a>
      </div>

      <ul className="qa-list">
        {items.map((it) => {
          const isOpen = open === it.id;
          return (
            <li key={it.id} className={`qa-item ${isOpen ? "open" : ""}`}>
              <button
                className="qa-q"
                type="button"
                onClick={() => setOpen(isOpen ? null : it.id)}
              >
                <span
                  className="qa-asker"
                  style={{ background: `oklch(0.78 0.12 ${it.asker.color})` }}
                >
                  {it.asker.name[0].toUpperCase()}
                </span>
                <div className="qa-q-body">
                  <div className="qa-q-meta mono">
                    <span>{it.asker.name}</span>
                    <span className="dot" />
                    <span>{it.askedAt}</span>
                    <span className="qa-tag">#{it.tag}</span>
                    {it.resolved && (
                      <span className="qa-resolved">
                        <I.shield /> 답변 완료
                      </span>
                    )}
                  </div>
                  <div className="qa-q-text">{it.question}</div>
                </div>
                <I.chev className="qa-chev" />
              </button>

              <div className="qa-a">
                <div className="qa-a-inner">
                  {it.answers.map((a, i) => (
                    <div key={i} className="qa-msg">
                      {a.isBot ? (
                        <Avatar />
                      ) : (
                        <span
                          className="qa-asker"
                          style={{ background: `oklch(0.78 0.12 ${it.asker.color})` }}
                        >
                          {a.who[0].toUpperCase()}
                        </span>
                      )}
                      <div>
                        <div className="qa-q-meta mono">
                          <span className={a.isBot ? "name bot" : "name"}>
                            {a.isBot ? "카미봇" : a.who}
                          </span>
                          {a.isBot && (
                            <span className="badge-bot is-verified">앱</span>
                          )}
                        </div>
                        <div className="qa-a-text">{a.body}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ============================================================
// Section wrapper
// ============================================================
export function LivePulse() {
  return (
    <section id="pulse">
      <div className="shell">
        <div className="section-head">
          <span className="section-eyebrow">LIVE · 매월 업데이트</span>
          <h2 className="section-title">잠깐, 이 봇 — 지금도 활발히 굴러가요.</h2>
          <p className="section-sub">
            매달 카미봇 공식 지원 서버에 올라오는 월간 공지, 그리고 거기서 오가는 진짜 질문과 답을 그대로 가져왔어요.
            취미 프로젝트가 아니라 매일 손이 닿는 서비스라는 걸, 글이 아니라 활동으로 보여드릴게요.
          </p>
        </div>

        <div className="pulse-grid">
          <AnnouncementCard data={MOCK_ANNOUNCEMENT} />
          <QAList items={MOCK_QA} />
        </div>
      </div>
    </section>
  );
}
